"use client"

import { useState } from "react"
import { submitFeedback } from "@/app/actions/searchActions"
import styles from "./search-feedback.module.css"

interface SearchFeedbackProps {
  searchId: string
  className?: string
}

export function SearchFeedback({ searchId, className }: SearchFeedbackProps) {
  const [feedback, setFeedback] = useState<"positive" | "negative" | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showThanks, setShowThanks] = useState(false)
  const [isVisible, setIsVisible] = useState(true)

  const handleFeedback = async (type: "positive" | "negative") => {
    if (isSubmitting || feedback) return

    setIsSubmitting(true)
    try {
      await submitFeedback(searchId, type)
      setFeedback(type)
      setShowThanks(true)
      setTimeout(() => {
        setIsVisible(false)
      }, 2000)
    } catch (error) {
      console.error("Failed to submit feedback:", error)
      setIsSubmitting(false)
    }
  }

  if (!isVisible) {
    return null
  }

  return (
    <div className={`${styles.container} ${className || ""}`}>
      <div className={styles.feedbackBox}>
        {showThanks ? (
          <span className={`${styles.promptText} ${styles.thanksText}`}>Thanks!</span>
        ) : (
          <>
            <span className={styles.promptText}>Promising results?</span>

            <div className={styles.emojiContainer}>
              <button
                onClick={() => handleFeedback("positive")}
                disabled={isSubmitting}
                title="Good result"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.00065 1.33398C4.33398 1.33398 1.33398 4.33398 1.33398 8.00065C1.33398 11.6673 4.33398 14.6673 8.00065 14.6673C11.6673 14.6673 14.6673 11.6673 14.6673 8.00065C14.6673 4.33398 11.6673 1.33398 8.00065 1.33398ZM8.00065 13.334C5.06732 13.334 2.66732 10.934 2.66732 8.00065C2.66732 5.06732 5.06732 2.66732 8.00065 2.66732C10.934 2.66732 13.334 5.06732 13.334 8.00065C13.334 10.934 10.934 13.334 8.00065 13.334Z" fill="#1E1C1C"/>
                    <path d="M10.6663 8.73264C10.333 8.53264 9.933 8.66597 9.733 8.99931C9.39967 9.59931 8.733 9.99931 7.99967 9.99931C7.26634 9.99931 6.59967 9.59931 6.26634 8.99931C6.06634 8.66597 5.66634 8.59931 5.333 8.73264C4.99967 8.93264 4.933 9.33264 5.06634 9.66597C5.66634 10.666 6.79967 11.3326 7.933 11.3326C9.06634 11.3326 10.1997 10.666 10.7997 9.66597C10.9997 9.33264 10.8663 8.93264 10.533 8.73264H10.6663Z" fill="#1E1C1C"/>
                    <path d="M6.00065 6.66732C6.36884 6.66732 6.66732 6.36884 6.66732 6.00065C6.66732 5.63246 6.36884 5.33398 6.00065 5.33398C5.63246 5.33398 5.33398 5.63246 5.33398 6.00065C5.33398 6.36884 5.63246 6.66732 6.00065 6.66732Z" fill="#1E1C1C"/>
                    <path d="M10.0007 6.66732C10.3688 6.66732 10.6673 6.36884 10.6673 6.00065C10.6673 5.63246 10.3688 5.33398 10.0007 5.33398C9.63246 5.33398 9.33398 5.63246 9.33398 6.00065C9.33398 6.36884 9.63246 6.66732 10.0007 6.66732Z" fill="#1E1C1C"/>
                </svg>         
              </button>
              <button
                onClick={() => handleFeedback("negative")}
                disabled={isSubmitting}
                title="Poor result"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.00065 1.33398C4.33398 1.33398 1.33398 4.33398 1.33398 8.00065C1.33398 11.6673 4.33398 14.6673 8.00065 14.6673C11.6673 14.6673 14.6673 11.6673 14.6673 8.00065C14.6673 4.33398 11.6673 1.33398 8.00065 1.33398ZM8.00065 13.334C5.06732 13.334 2.66732 10.934 2.66732 8.00065C2.66732 5.06732 5.06732 2.66732 8.00065 2.66732C10.934 2.66732 13.334 5.06732 13.334 8.00065C13.334 10.934 10.934 13.334 8.00065 13.334Z" fill="#1E1C1C"/>
                    <path d="M7.99996 8C6.79996 8 5.66663 8.66667 5.06663 9.66667C4.86663 10 4.99996 10.4 5.33329 10.6C5.66663 10.8 6.06663 10.6667 6.26663 10.3333C6.59996 9.73333 7.26663 9.33333 7.99996 9.33333C8.73329 9.33333 9.39996 9.73333 9.73329 10.3333C9.86663 10.5333 10.0666 10.6667 10.3333 10.6667C10.6 10.6667 10.5333 10.6667 10.6666 10.6C11 10.4 11.0666 10 10.9333 9.66667C10.3333 8.66667 9.19996 8 7.99996 8Z" fill="#1E1C1C"/>
                    <path d="M6.00065 6.66732C6.36884 6.66732 6.66732 6.36884 6.66732 6.00065C6.66732 5.63246 6.36884 5.33398 6.00065 5.33398C5.63246 5.33398 5.33398 5.63246 5.33398 6.00065C5.33398 6.36884 5.63246 6.66732 6.00065 6.66732Z" fill="#1E1C1C"/>
                    <path d="M10.0007 6.66732C10.3688 6.66732 10.6673 6.36884 10.6673 6.00065C10.6673 5.63246 10.3688 5.33398 10.0007 5.33398C9.63246 5.33398 9.33398 5.63246 9.33398 6.00065C9.33398 6.36884 9.63246 6.66732 10.0007 6.66732Z" fill="#1E1C1C"/>
                </svg>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
